import type { PropsWithChildren, ReactElement } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

let id = 0;
const getNextId = () => id++;

type ToastVariant = "success" | "error" | "default";

type ToastProps = {
  id: string;
  message: string | ReactElement;
  title?: string | ReactElement;
  autoDismiss: boolean;
  autoDismissTimeout: number;
  variant: ToastVariant;
};

type AddToast = (
  message: string,
  options?: {
    variant?: ToastVariant;
    duration?: number;
    autoDismiss?: boolean;
    title?: string;
  },
) => void;

const DEFAULT_DURATIONS: Record<ToastVariant, number> = {
  default: 3000,
  success: 3000,
  error: 15000,
};

const DEFAULT_TITLES: Record<ToastVariant, string | undefined> = {
  default: undefined,
  success: "Success!",
  error: "Whoops!",
};

const AddToastContext = createContext<AddToast>(() => {
  throw new Error("Must be called from within a ToastProvider");
});

export const ToastContext = createContext<{
  getUpTo: (limit: number) => ToastProps[];
  removeById: (id: string) => void;
}>({
  getUpTo: (): ToastProps[] => {
    console.warn("Not called from within provider");
    return [];
  },
  removeById: () => {
    console.warn("Not called from within provider");
  },
});

export function useAddToast() {
  return useContext(AddToastContext);
}

export const ToastProvider = ({
  children,
  ...props
}: PropsWithChildren<unknown>) => {
  const { add, getUpTo, removeById } = useToastState<ToastProps>();

  const addToast = useCallback<AddToast>(
    (
      message: string,
      {
        variant = "default",
        autoDismiss = true,
        duration = DEFAULT_DURATIONS[variant],
        title = DEFAULT_TITLES[variant],
      }: Parameters<AddToast>[1] = {},
    ) => {
      add({
        id: getNextId().toString(),
        message: message,
        autoDismiss,
        autoDismissTimeout: duration,
        title,
        variant,
      });
    },
    [add],
  );

  const values = useMemo(
    () => ({ getUpTo, removeById }),
    [getUpTo, removeById],
  );

  return (
    <AddToastContext.Provider value={addToast} {...props}>
      <ToastContext.Provider value={values}>{children}</ToastContext.Provider>
    </AddToastContext.Provider>
  );
};

function useToastState<T extends { id: string }>() {
  const [items, setItems] = useState<T[]>([]);

  const add = useCallback((item: T) => {
    setItems((current) => [...current, item]);
  }, []);

  const getUpTo = useCallback(
    (limit: number) => {
      return items.slice(0, limit);
    },
    [items],
  );

  const removeById = useCallback((id: string) => {
    setItems((current) => current.filter((it) => it.id !== id));
  }, []);

  return { add, getUpTo, removeById };
}
